import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Input } from "@/shared/components/Input";
import { Button } from "@/shared/components/Button";
import { BotaoVoltar } from "@/shared/components/BotaoVoltar";
import type { FuncaoMinisterial } from "@/core/services/ministeriosService";

// Limite espelhando a coluna do banco (migration 0016): funcoes_ministeriais.nome varchar(100).
const LIMITE_NOME = 100;

const schema = z.object({
  nome: z
    .string()
    .min(2, "Nome muito curto.")
    .max(LIMITE_NOME, `O nome pode ter no máximo ${LIMITE_NOME} caracteres.`),
  ehLider: z.boolean(),
  podeGerenciarModulo: z.boolean(),
});

type Valores = z.infer<typeof schema>;

interface FormularioFuncaoProps {
  funcaoEmEdicao?: FuncaoMinisterial | null;
  carregando: boolean;
  erro?: string | null;
  onSubmit: (dados: Valores) => void;
}

/**
 * Formulário de função ministerial (DA-001). Os dois marcadores são os
 * portões do modelo de autorização:
 *   - eh_lider: governança do ministério (DA-004);
 *   - pode_gerenciar_modulo: Nível 2 do DA-002 (gestão dos módulos sob
 *     responsabilidade do ministério).
 */
export function FormularioFuncao({
  funcaoEmEdicao,
  carregando,
  erro,
  onSubmit,
}: FormularioFuncaoProps) {
  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<Valores>({
    resolver: zodResolver(schema),
    defaultValues: { nome: "", ehLider: false, podeGerenciarModulo: false },
  });

  useEffect(() => {
    if (funcaoEmEdicao) {
      reset({
        nome: funcaoEmEdicao.nome,
        ehLider: funcaoEmEdicao.eh_lider,
        podeGerenciarModulo: funcaoEmEdicao.pode_gerenciar_modulo,
      });
    }
  }, [funcaoEmEdicao, reset]);

  const nomeAtual = watch("nome") ?? "";

  function submit(dados: Valores) {
    onSubmit({ ...dados, nome: dados.nome.trim() });
  }

  return (
    <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4" noValidate>
      <div className="flex flex-col gap-1">
        <Input
          label="Nome da função"
          placeholder="Ex.: Líder, Gestor, Guitarrista"
          maxLength={LIMITE_NOME}
          erro={errors.nome?.message}
          {...register("nome")}
        />
        <p
          className={`text-right text-xs ${
            nomeAtual.length >= LIMITE_NOME ? "font-medium text-warning" : "text-muted"
          }`}
        >
          {nomeAtual.length}/{LIMITE_NOME}
        </p>
      </div>

      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-surface p-3">
        <input type="checkbox" className="mt-0.5 h-4 w-4 accent-primary" {...register("ehLider")} />
        <span>
          <span className="block text-sm font-semibold text-text">Lidera o ministério</span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted">
            Quem tem esta função convida e remove integrantes e define as funções do ministério.
          </span>
        </span>
      </label>

      <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-border bg-surface p-3">
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-primary"
          {...register("podeGerenciarModulo")}
        />
        <span>
          {/* F2-07 (revisão 20/07/2026): nome encurtado — "Gerencia os módulos" soava
              redundante ao lado da explicação logo abaixo. */}
          <span className="block text-sm font-semibold text-text">Gestor de elo</span>
          <span className="mt-0.5 block text-xs leading-relaxed text-muted">
            Quem tem esta função gerencia os elos sob responsabilidade deste ministério (ex.:
            aprovar empréstimos da Biblioteca).
          </span>
        </span>
      </label>

      {erro && (
        <p role="alert" className="rounded-md bg-danger/10 px-3 py-2 text-sm text-danger">
          {erro}
        </p>
      )}

      <Button type="submit" carregando={carregando} className="mt-2">
        {funcaoEmEdicao ? "Salvar alterações" : "Criar função"}
      </Button>
      <BotaoVoltar disabled={carregando} className="-mt-2" />
    </form>
  );
}
